import React from 'react';
import { toArrayStrings } from 'common-utils/string-utils';
import { getErrorMessage } from 'common-utils';

export interface IAlertMessagesProps {
    errorMessages: string[];
    warningMessages: string[];
    infoMessages: string[];
    addErrorMessages: (messages: string | string[]) => void;
    addWarningMessages: (messages: string | string[]) => void;
    addInfoMessages: (messages: string | string[]) => void;
    clearMessages: () => void;
    clearErrorMessages: () => void;
    clearWarningMessages: () => void;
    clearInfoMessages: () => void;
    runWithErrorHandling: <T>(callback: () => T | Promise<T>) => Promise<T | undefined>;
}
interface IAlertMessages {
    errorMessages: string[];
    warningMessages: string[];
    infoMessages: string[];
}
interface IAlertMessagesAction {
    type: 'error' | 'warning' | 'info' | 'clear-all' | 'clear-error' | 'clear-warning' | 'clear-info';
    payload?: string | string[];
}
export const useAlertMessages = (): IAlertMessagesProps => {
    const [messages, dispatch] = React.useReducer<React.Reducer<IAlertMessages, IAlertMessagesAction>>(
        (state: IAlertMessages, action: IAlertMessagesAction): IAlertMessages => {
            switch (action.type) {
                case 'error':
                    return {
                        ...state,
                        errorMessages: [...state.errorMessages, ...toArrayStrings(action.payload)],
                    };
                case 'warning':
                    return {
                        ...state,
                        warningMessages: [...state.warningMessages, ...toArrayStrings(action.payload)],
                    };
                case 'info':
                    return {
                        ...state,
                        infoMessages: [...state.infoMessages, ...toArrayStrings(action.payload)],
                    };
                case 'clear-error':
                    return { ...state, errorMessages: [] };
                case 'clear-warning':
                    return { ...state, warningMessages: [] };
                case 'clear-info':
                    return { ...state, infoMessages: [] };
                case 'clear-all':
                    return { errorMessages: [], warningMessages: [], infoMessages: [] };
                default:
                    return state;
            }
        },
        {
            errorMessages: [],
            warningMessages: [],
            infoMessages: [],
        },
    );

    return {
        ...messages,
        addErrorMessages: (messages: string | string[]): void => dispatch({ type: 'error', payload: messages }),
        addWarningMessages: (messages: string | string[]): void => dispatch({ type: 'warning', payload: messages }),
        addInfoMessages: (messages: string | string[]): void => dispatch({ type: 'info', payload: messages }),
        clearMessages: (): void => dispatch({ type: 'clear-all' }),
        clearErrorMessages: (): void => dispatch({ type: 'clear-error' }),
        clearWarningMessages: (): void => dispatch({ type: 'clear-warning' }),
        clearInfoMessages: (): void => dispatch({ type: 'clear-info' }),
        runWithErrorHandling: async (callback: () => any) => {
            dispatch({ type: 'clear-error' });
            try {
                return await callback();
            } catch (err) {
                dispatch({ type: 'error', payload: getErrorMessage(err) })
            }
        },
    };
};

